import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { ChevronRight, Settings, Plug, BarChart2, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const spinStyle = document.createElement('style');
if (!document.getElementById('adpulse-spin')) {
  spinStyle.id = 'adpulse-spin';
  spinStyle.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(spinStyle);
}

export default function ClientWorkspace() {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAccountWizard, setShowAccountWizard] = useState(false);
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [selectedMetaAccounts, setSelectedMetaAccounts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [connectingMeta, setConnectingMeta] = useState(false);
  const [showGoogleAdsModal, setShowGoogleAdsModal] = useState(false);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [loadingGoogleAdsAccounts, setLoadingGoogleAdsAccounts] = useState(false);
  const [savingGoogleAdsAccount, setSavingGoogleAdsAccount] = useState(false);
  // GA4
  const [showGa4Modal, setShowGa4Modal] = useState(false);
  const [ga4Properties, setGa4Properties] = useState([]);
  const [loadingGa4, setLoadingGa4] = useState(false);
  const [savingGa4, setSavingGa4] = useState(false);
  // GSC
  const [showGscModal, setShowGscModal] = useState(false);
  const [gscSites, setGscSites] = useState([]);
  const [loadingGsc, setLoadingGsc] = useState(false);
  const [savingGsc, setSavingGsc] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [showOrganicoModal, setShowOrganicoModal] = useState(false);
  const [organicoType, setOrganicoType] = useState('facebook'); // 'facebook' | 'instagram'
  const [organicoPages, setOrganicoPages] = useState([]);
  const [organicoIgAccounts, setOrganicoIgAccounts] = useState([]);
  const [loadingOrganico, setLoadingOrganico] = useState(false);
  const [selectedFbPage, setSelectedFbPage] = useState(null);
  const [selectedIgAccount, setSelectedIgAccount] = useState(null);
  const [savingOrganico, setSavingOrganico] = useState(false);
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <BarChart2 className="w-4 h-4" /> },
    { id: 'sources', label: 'Fuentes de datos', icon: <Plug className="w-4 h-4" /> },
    { id: 'reports', label: 'Informes', icon: <FileText className="w-4 h-4" /> },
    { id: 'settings', label: 'Ajustes', icon: <Settings className="w-4 h-4" /> },
  ];

  useEffect(() => {
    fetchClient();
    fetchCampaigns();
    fetchIntegrations();
    
    const params = new URLSearchParams(location.search);
    
    // Check if returning from Google Ads OAuth
    if (params.get('ga4_connected')) {
      alert('✅ Google Analytics 4 conectado. Ahora selecciona la propiedad desde Fuentes.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchClient();
    }
    if (params.get('gsc_connected')) {
      alert('✅ Search Console conectado. Ahora selecciona el sitio desde Fuentes.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchClient();
    }
    if (params.get('ga4_error')) {
      alert('❌ Error conectando GA4: ' + params.get('ga4_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('gsc_error')) {
      alert('❌ Error conectando GSC: ' + params.get('gsc_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('google_ads_connected')) {
      alert('✅ Google Ads conectado correctamente.');
      window.history.replaceState({}, '', window.location.pathname);
      // Refrescar cliente
      fetchClient();
    }
    if (params.get('google_ads_error')) {
      alert('❌ Error conectando Google Ads: ' + params.get('google_ads_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Check if returning from Meta OAuth
    if (params.get('meta') === 'success') {
      window.history.replaceState({}, '', `/clients/${id}`);
      setConnectingMeta(true);
      loadMetaAccounts();
    }
  }, [id]);

  const loadMetaAccounts = async () => {
    const h = { Authorization: `Bearer ${token}` };
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      const r = await fetch(`${base}/api/meta/personal-ad-accounts?client_id=${id}`, { headers: h });
      const d = await r.json();
      setMetaAccounts(d.data || []);
      setConnectingMeta(false);
      setShowAccountWizard(true);
    } catch(e) {}
  };

  const openGoogleAdsAccountModal = async () => {
    setShowConnectModal(false);
    setShowGoogleAdsModal(true);
    setLoadingGoogleAdsAccounts(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/google-ads/accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setGoogleAdsAccounts(d.accounts || []);
    } catch (e) {
      console.error(e);
      alert('Error cargando cuentas Google Ads');
    } finally {
      setLoadingGoogleAdsAccounts(false);
    }
  };

  const selectGoogleAdsAccount = async (account) => {
    setSavingGoogleAdsAccount(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/google-ads/select-account`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: id,
          customer_id: account.customer_id,
          account_name: account.name,
          login_customer_id: account.customer_id
        })
      });
      if (res.ok) {
        setShowGoogleAdsModal(false);
        fetchClient();
        alert('✅ Cuenta Google Ads vinculada: ' + account.name + '. Sincronizando datos...');
        fetch(process.env.REACT_APP_BACKEND_URL + '/api/google-ads/sync/' + id, {method: 'POST', headers: {Authorization: 'Bearer ' + token}}).catch(() => {});
      } else {
        alert('Error guardando cuenta');
      }
    } catch (e) {
      console.error(e);
      alert('Error');
    } finally {
      setSavingGoogleAdsAccount(false);
    }
  };

  const openGa4Modal = async () => {
    setShowGa4Modal(true);
    setLoadingGa4(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/ga4-client/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setGa4Properties(d.properties || []);
    } catch (e) { alert('Error cargando propiedades GA4'); }
    finally { setLoadingGa4(false); }
  };

  const selectGa4Property = async (prop) => {
    setSavingGa4(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/ga4-client/select-property`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: id,
          property_id: prop.property_id,
          property_name: prop.property_name,
          account_name: prop.account_name
        })
      });
      if (res.ok) {
        setShowGa4Modal(false);
        fetchClient();
        alert('✅ Propiedad GA4 vinculada: ' + prop.property_name);
      } else { alert('Error guardando propiedad'); }
    } catch (e) { alert('Error'); }
    finally { setSavingGa4(false); }
  };

  const handleConnectGa4 = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/ga4-client/auth-url?client_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.auth_url) window.location.href = d.auth_url;
    } catch (e) { alert('Error GA4'); }
  };

  const openGscModal = async () => {
    setShowGscModal(true);
    setLoadingGsc(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/gsc-client/sites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      setGscSites(d.sites || []);
    } catch (e) { alert('Error cargando sitios GSC'); }
    finally { setLoadingGsc(false); }
  };

  const selectGscSite = async (site) => {
    setSavingGsc(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/gsc-client/select-site`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: id,
          site_url: site.site_url,
          site_name: site.site_name
        })
      });
      if (res.ok) {
        setShowGscModal(false);
        fetchClient();
        alert('✅ Sitio GSC vinculado: ' + site.site_name);
      } else { alert('Error guardando sitio'); }
    } catch (e) { alert('Error'); }
    finally { setSavingGsc(false); }
  };

  const handleConnectGsc = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/gsc-client/auth-url?client_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.auth_url) window.location.href = d.auth_url;
    } catch (e) { alert('Error GSC'); }
  };

  const handleConnectGoogleAds = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/google-ads/auth-url?client_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.auth_url) {
        window.location.href = d.auth_url;
      } else {
        alert('Error generando URL de autorización');
      }
    } catch (e) {
      console.error(e);
      alert('Error al conectar Google Ads');
    }
  };

  const syncMetaAccounts = async () => {
    if (selectedMetaAccounts.length === 0) return;
    setSyncing(true);
    const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${base}/api/meta/save-accounts?client_id=${id}`, {
        method: 'POST',
        headers: h,
        body: JSON.stringify({ ad_account_ids: selectedMetaAccounts })
      });
      setSyncing(false);
      setSyncSuccess(true);
      fetchIntegrations();
      fetchCampaigns();
      fetchClient();
      setTimeout(() => {
        setShowAccountWizard(false);
        setSyncSuccess(false);
        navigate('/clients');
      }, 2500);
    } catch(e) {
      setSyncing(false);
    }
  };

  const fetchClient = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } });
      const found = res.data.find(c => c.id === id);
      if (!found) { navigate('/clients'); return; }
      setClient(found);
    } catch (e) { navigate('/clients'); }
    finally { setLoading(false); }
  };

  const [metaStatus, setMetaStatus] = useState({ connected: false });
  const [googleStatus, setGoogleStatus] = useState({ connected: false });
  const [ga4Status, setGa4Status] = useState({ connected: false });
  const [gscStatus, setGscStatus] = useState({ connected: false });

  const fetchIntegrations = async () => {
    const h = { Authorization: `Bearer ${token}` };
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      const [meta, google, ga4, gsc] = await Promise.all([
        fetch(`${base}/api/meta/status?client_id=${id}`, { headers: h }).then(r => r.json()),
        fetch(`${base}/api/google/status?client_id=${id}`, { headers: h }).then(r => r.json()),
        fetch(`${base}/api/ga4/status?client_id=${id}`, { headers: h }).then(r => r.json()),
        fetch(`${base}/api/gsc/status?client_id=${id}`, { headers: h }).then(r => r.json()),
      ]);
      setMetaStatus(meta);
      setGoogleStatus(google);
      setGa4Status(ga4);
      setGscStatus(gsc);
    } catch(e) {}
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/by-workspace/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch (e) {}
  };

  const fmt = (n, type) => {
    if (!n && n !== 0) return '—';
    if (type === 'usd') return n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${parseFloat(n).toFixed(0)}`;
    if (type === 'pct') return `${parseFloat(n).toFixed(2)}%`;
    return n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(n);
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 text-sm">Cargando datos del cliente...</p>
      </div>
    </DashboardLayout>
  );

  const totalSpent = campaigns.reduce((s,c) => s+(parseFloat(c.spent)||0), 0);
  const totalClicks = campaigns.reduce((s,c) => s+(parseInt(c.clicks)||0), 0);
  const totalImpressions = campaigns.reduce((s,c) => s+(parseInt(c.impressions)||0), 0);
  const totalConversions = campaigns.reduce((s,c) => s+(parseInt(c.conversions)||0), 0);
  const avgCtr = campaigns.length ? campaigns.reduce((s,c) => s+(parseFloat(c.ctr)||0), 0)/campaigns.length : 0;

  const handleDisconnectMeta = async () => {
    if (!window.confirm('¿Desconectar Meta Ads?')) return;
    try {
      await axios.delete(`${API}/meta/disconnect?client_id=${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setClient(prev => ({ ...prev, ad_account_id: null }));
    } catch(e) { alert('Error al desconectar Meta'); }
  };

  const handleDisconnectGoogleAds = async () => {
    if (!window.confirm('¿Desconectar Google Ads?')) return;
    try {
      await axios.delete(`${API}/google-ads/disconnect?client_id=${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setClient(prev => ({ ...prev, google_ads_refresh_token: null, google_ads_customer_id: null }));
    } catch(e) { alert('Error al desconectar Google Ads'); }
  };

  const handleDisconnectGa4 = async () => {
    if (!window.confirm('¿Desconectar Google Analytics 4?')) return;
    try {
      await axios.delete(`${API}/ga4/disconnect?client_id=${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setClient(prev => ({ ...prev, ga4_refresh_token: null, ga4_property_id: null, ga4_property_name: null }));
    } catch(e) { alert('Error al desconectar GA4'); }
  };

  const handleDisconnectGsc = async () => {
    if (!window.confirm('¿Desconectar Search Console?')) return;
    try {
      await axios.delete(`${API}/gsc/disconnect?client_id=${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setClient(prev => ({ ...prev, gsc_refresh_token: null, gsc_site_url: null, gsc_site_name: null }));
    } catch(e) { alert('Error al desconectar GSC'); }
  };

  const openOrganicoModal = async () => {
    setLoadingOrganico(true);
    setShowOrganicoModal(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const h = { Authorization: `Bearer ${token}` };
      const r = await fetch(`${base}/api/meta/pages?client_id=${id}`, { headers: h });
      const d = await r.json();
      setOrganicoPages(d.pages || []);
      setOrganicoIgAccounts(d.instagram_accounts || []);
    } catch(e) { alert('Error cargando páginas: ' + e.message); }
    setLoadingOrganico(false);
  };

  const saveOrganico = async () => {
    setSavingOrganico(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const body = organicoType === 'facebook'
        ? { client_id: id, facebook_page_id: selectedFbPage?.id, facebook_page_name: selectedFbPage?.name }
        : { client_id: id, instagram_account_id: selectedIgAccount?.id, instagram_username: selectedIgAccount?.username };
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meta/save-organic`, { method: 'POST', headers: h, body: JSON.stringify(body) });
      setShowOrganicoModal(false);
      window.location.reload();
    } catch(e) { alert('Error guardando: ' + e.message); }
    setSavingOrganico(false);
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="px-8 pt-6 pb-0">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <button onClick={() => navigate('/clients')} className="hover:text-gray-900 font-medium">Clientes</button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900 font-semibold">{client?.name}</span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                  {client?.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{client?.name}</h1>
                  {client?.website && <p className="text-sm text-gray-500">{client.website}</p>}
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${activeTab===tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-8 py-6">
          {activeTab === 'dashboard' && (
            campaigns.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                <BarChart2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Sin datos aún</h3>
                <p className="text-gray-500 mb-4">Conecta una fuente de datos para ver métricas</p>
                <button onClick={() => setActiveTab('sources')} className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">Conectar fuente de datos</button>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  {[['Gasto Total',fmt(totalSpent,'usd'),'#6366F1'],['Impresiones',fmt(totalImpressions),'#1877F2'],['Clicks',fmt(totalClicks),'#1e293b'],['CTR',fmt(avgCtr,'pct'),'#6366F1'],['Conversiones',fmt(totalConversions),'#10B981']].map(([label,value,color]) => (
                    <div key={label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                      <p className="text-2xl font-extrabold" style={{color}}>{value}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900">Campañas ({campaigns.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {['Campaña','Plataforma','Gasto','Impresiones','Clicks','CTR','Conv.'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c,i) => (
                          <tr key={c.id} className={i%2===0?'bg-white':'bg-gray-50/50'}>
                            <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{c.name}</td>
                            <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.platform==='meta'?'bg-blue-100 text-blue-700':'bg-red-100 text-red-700'}`}>{c.platform==='meta'?'Meta':'Google'}</span></td>
                            <td className="px-4 py-3 text-indigo-600 font-semibold">{fmt(parseFloat(c.spent),'usd')}</td>
                            <td className="px-4 py-3 text-gray-600">{fmt(parseInt(c.impressions))}</td>
                            <td className="px-4 py-3 text-gray-600">{fmt(parseInt(c.clicks))}</td>
                            <td className="px-4 py-3 text-indigo-600">{fmt(parseFloat(c.ctr),'pct')}</td>
                            <td className="px-4 py-3 text-green-600 font-semibold">{c.conversions||0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          )}

          {activeTab === 'sources' && (
            <div>
              <h2 style={{fontSize:18,fontWeight:700,color:'#1e293b',marginBottom:20}}>Fuentes de datos</h2>

              <p style={{fontSize:11,fontWeight:600,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:14}}>Disponibles</p>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:20,marginBottom:36}}>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#1877F2',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Facebook Orgánico</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Páginas y publicaciones</p></div>
                  </div>
                  {metaStatus?.facebook_page_id
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ {metaStatus.facebook_page_name || 'Conectado'}</span>
                    : <button onClick={async()=>{if(!metaStatus?.connected){alert('Primero conecta Meta Ads');return;}setOrganicoType('facebook');await openOrganicoModal();}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'#1877F2',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Facebook</span><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg></button>}
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'linear-gradient(45deg,#f09433,#dc2743,#bc1888)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Instagram Orgánico</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Posts y stories</p></div>
                  </div>
                  {metaStatus?.instagram_account_id
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ @{metaStatus.instagram_username}</span>
                    : <button onClick={async()=>{if(!metaStatus?.connected){alert('Primero conecta Meta Ads');return;}setOrganicoType('instagram');await openOrganicoModal();}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'linear-gradient(45deg,#f09433,#dc2743,#bc1888)',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Instagram</span><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg></button>}
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#1877F2',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Meta Ads</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Facebook e Instagram Ads</p></div>
                  </div>
                  {client?.ad_account_id
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ Conectado</span>
                    : <button onClick={async()=>{const base=process.env.REACT_APP_BACKEND_URL;const h={Authorization:`Bearer ${token}`};const r=await fetch(`${base}/api/auth/meta/login?client_id=${id}`,{headers:h});const d=await r.json();if(d.url){const popup=window.open(d.url,'meta_auth','width=600,height=700');const handler=(e)=>{if(e.data?.type==='meta_connected'){window.removeEventListener('message',handler);clearInterval(pollTimer);popup?.close();setConnectingMeta(true);loadMetaAccounts();}};window.addEventListener('message',handler);const pollTimer=setInterval(()=>{if(popup?.closed){clearInterval(pollTimer);window.removeEventListener('message',handler);setConnectingMeta(false);}},500);}}} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'#1877F2',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Meta Ads</span><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg></button>}
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'white',border:'0.5px solid #e2e8f0',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="22" height="20" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Google Ads</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Search y Display</p></div>
                  </div>
                  {client?.google_ads_refresh_token
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ Conectado</span>
                    : <button onClick={handleConnectGoogleAds} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'#4285F4',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Google Ads</span><svg width="16" height="16" viewBox="0 0 48 48"><path fill="white" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="white" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="white" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="white" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg></button>}
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#E37400',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="14" width="4" height="8" rx="1" fill="white"/><rect x="10" y="8" width="4" height="14" rx="1" fill="white" opacity="0.8"/><rect x="18" y="2" width="4" height="20" rx="1" fill="white"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Google Analytics 4</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Sesiones y usuarios</p></div>
                  </div>
                  {client?.ga4_refresh_token
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ Conectado</span>
                    : <button onClick={handleConnectGa4} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'#E37400',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Analytics 4</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="14" width="4" height="8" rx="1" fill="white"/><rect x="10" y="8" width="4" height="14" rx="1" fill="white" opacity="0.8"/><rect x="18" y="2" width="4" height="20" rx="1" fill="white"/></svg></button>}
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #e2e8f0',padding:'22px 22px 18px',background:'white',display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#34A853',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:15,color:'#1e293b'}}>Search Console</p><p style={{margin:0,fontSize:12,color:'#94a3b8'}}>Clicks e impresiones</p></div>
                  </div>
                  {client?.gsc_refresh_token
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'14px 0',borderRadius:12,background:'#dcfce7',color:'#15803d',fontSize:12,fontWeight:600}}>✓ Conectado</span>
                    : <button onClick={handleConnectGsc} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderRadius:12,border:'none',background:'#34A853',color:'white',fontSize:14,fontWeight:500,cursor:'pointer',width:'100%'}}><span>Conectar Search Console</span><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg></button>}
                </div>

              </div>

              <p style={{fontSize:11,fontWeight:600,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:14}}>Próximamente</p>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:20}}>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#000',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>TikTok Ads</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Campañas TikTok</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#000',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>TikTok Orgánico</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Cuenta personal/business</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#FF0000',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>YouTube</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Videos y campañas</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#0A66C2',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>LinkedIn Ads</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Campañas LinkedIn</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#E60023',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>Pinterest Ads</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Campañas Pinterest</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'#000',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>X / Twitter Ads</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Campañas Twitter</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

                <div style={{borderRadius:14,border:'0.5px solid #f1f5f9',padding:'22px 22px 18px',background:'#fafafa',display:'flex',flexDirection:'column',gap:14,opacity:0.7}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:42,height:42,borderRadius:12,background:'white',border:'0.5px solid #e2e8f0',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><svg width="22" height="22" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg></div>
                    <div><p style={{margin:0,fontWeight:600,fontSize:14,color:'#94a3b8'}}>Google My Business</p><p style={{margin:0,fontSize:11,color:'#cbd5e1'}}>Reseñas y perfil local</p></div>
                  </div>
                  <div style={{padding:'9px 14px',borderRadius:10,background:'#f1f5f9',color:'#94a3b8',fontSize:12,fontWeight:500,textAlign:'center'}}>Próximamente</div>
                </div>

              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium mb-2">Reportes del cliente</p>
              <p className="text-gray-400 text-sm">Próximamente</p>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 max-w-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Información del cliente</h3>
              <div className="space-y-3 text-sm">
                {[['Nombre',client?.name],['Empresa',client?.company],['Industria',client?.industry],['Sitio Web',client?.website],['Notas',client?.notes]].map(([k,v]) => v ? (
                  <div key={k} className="flex gap-3"><span className="text-gray-400 w-24 flex-shrink-0">{k}</span><span className="text-gray-900">{v}</span></div>
                ) : null)}
              </div>
              <button onClick={() => navigate('/clients')} className="mt-6 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">← Volver a clientes</button>
            </div>
          )}
        </div>
      </div>
      {connectingMeta && !showAccountWizard && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999}}>
          <div style={{background:'white',borderRadius:16,padding:'40px 50px',textAlign:'center',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <div style={{width:48,height:48,border:'4px solid #e0e7ff',borderTopColor:'#6366f1',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}}></div>
            <p style={{fontSize:16,fontWeight:600,color:'#1e293b',margin:'0 0 4px'}}>Conectando con Meta Ads...</p>
            <p style={{fontSize:13,color:'#94a3b8',margin:0}}>Autenticando y sincronizando datos</p>
          </div>
        </div>
      )}

      {showAccountWizard && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:16,padding:32,width:520,maxWidth:'90vw'}}>
            {syncSuccess ? (
              <div style={{textAlign:'center',padding:'30px 0'}}>
                <div style={{width:64,height:64,borderRadius:'50%',background:'#ECFDF5',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h3 style={{fontSize:20,fontWeight:700,color:'#1e293b',margin:'0 0 8px'}}>¡Cuenta conectada!</h3>
                <p style={{color:'#64748b',fontSize:14,margin:0}}>Meta Ads se sincronizó correctamente. Redirigiendo al dashboard...</p>
              </div>
            ) : (<>
            <h3 style={{fontSize:18,fontWeight:700,color:'#1e293b',margin:'0 0 8px'}}>Selecciona la cuenta de Meta Ads</h3>
            <p style={{color:'#64748b',fontSize:14,marginBottom:20}}>Elige qué cuenta publicitaria asignar a <strong>{client?.name}</strong></p>
            <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:300,overflowY:'auto',marginBottom:20}}>
              {metaAccounts.length === 0 ? (
                <p style={{color:'#94a3b8',textAlign:'center',padding:20}}>Cargando cuentas...</p>
              ) : metaAccounts.map(acc => (
                <label key={acc.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:10,border:`2px solid ${selectedMetaAccounts.includes(acc.id)?'#6366F1':'#e2e8f0'}`,cursor:'pointer',background:selectedMetaAccounts.includes(acc.id)?'#F0F0FF':'white'}}>
                  <input type="checkbox" checked={selectedMetaAccounts.includes(acc.id)}
                    onChange={e => setSelectedMetaAccounts(prev => e.target.checked ? [...prev, acc.id] : prev.filter(x => x !== acc.id))}
                    style={{width:16,height:16,accentColor:'#6366F1'}} />
                  <div>
                    <p style={{margin:0,fontWeight:600,color:'#1e293b',fontSize:14}}>{acc.name}</p>
                    <p style={{margin:0,color:'#94a3b8',fontSize:12}}>{acc.id}</p>
                  </div>
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setShowAccountWizard(false)} style={{flex:1,padding:'10px',borderRadius:8,border:'1px solid #e2e8f0',background:'white',cursor:'pointer',fontWeight:600,color:'#64748b'}}>Cancelar</button>
              <button onClick={syncMetaAccounts} disabled={selectedMetaAccounts.length===0||syncing}
                style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:selectedMetaAccounts.length>0?'#6366F1':'#e2e8f0',color:selectedMetaAccounts.length>0?'white':'#94a3b8',cursor:selectedMetaAccounts.length>0?'pointer':'not-allowed',fontWeight:700,fontSize:14}}>
                {syncing ? 'Sincronizando...' : `Sincronizar ${selectedMetaAccounts.length > 0 ? `(${selectedMetaAccounts.length})` : ''} cuentas`}
              </button>
            </div>
            </>)}
          </div>
        </div>
      )}
      {false && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:16,padding:32,width:480,maxWidth:'90vw'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
              <h3 style={{fontSize:18,fontWeight:700,color:'#1e293b',margin:0}}>Conectar fuente de datos</h3>
              <button onClick={() => setShowConnectModal(false)} style={{border:'none',background:'none',fontSize:20,cursor:'pointer',color:'#94a3b8'}}>×</button>
            </div>
            <p style={{color:'#64748b',fontSize:14,marginBottom:20}}>Selecciona qué plataforma conectar para <strong>{client?.name}</strong></p>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              {[
                { label:'Meta Ads', sub:'Facebook e Instagram', color:'#1877F2', letter:'f', key:'meta', status: metaStatus },
                { label:'Google Ads', sub:'Search y Display', color:'#EA4335', letter:'G', key:'google', status: googleStatus },
                { label:'Google Analytics 4', sub:'Sesiones y usuarios', color:'#F9AB00', letter:'A', key:'ga4', status: ga4Status },
                { label:'Search Console', sub:'Clicks e impresiones', color:'#34A853', letter:'S', key:'gsc', status: gscStatus },
              ].map(({label,sub,color,letter,key,status}) => (
                <div key={key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderRadius:10,border:'1px solid #e2e8f0'}}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:36,height:36,borderRadius:8,background:color,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:700,fontSize:14}}>{letter}</div>
                    <div>
                      <p style={{margin:0,fontWeight:600,color:'#1e293b',fontSize:14}}>{label}</p>
                      <p style={{margin:0,color:'#94a3b8',fontSize:12}}>{sub}</p>
                    </div>
                  </div>
                  {status?.connected
                    ? <span style={{padding:'4px 10px',background:'#dcfce7',color:'#16a34a',borderRadius:20,fontSize:12,fontWeight:600}}>✓ Conectado</span>
                    : <button onClick={async () => {
                          setShowConnectModal(false);
                          const h = { Authorization: `Bearer ${token}` };
                          const base = process.env.REACT_APP_BACKEND_URL;
                          if (key === 'meta') {
                            setConnectingMeta(true);
                            const r = await fetch(`${base}/api/auth/meta/login?client_id=${id}`, { headers: h });
                            const d = await r.json();
                            if (d.url) {
                              const popup = window.open(d.url, 'meta_auth', 'width=600,height=700');
                              const handler = (e) => {
                                if (e.data?.type === 'meta_connected') {
                                  window.removeEventListener('message', handler);
                                  popup?.close();
                                  loadMetaAccounts();
                                }
                              };
                              window.addEventListener('message', handler);
                            }
                          } else if (key === 'google') {
                            // Google Ads (ya implementado en handleConnectGoogleAds)
                            handleConnectGoogleAds();
                          } else if (key === 'ga4') {
                            const r = await fetch(`${base}/api/ga4-client/auth-url?client_id=${id}`, { headers: h });
                            const d = await r.json();
                            if (d.auth_url) window.location.href = d.auth_url;
                            else alert('Error generando URL GA4');
                          } else if (key === 'gsc') {
                            const r = await fetch(`${base}/api/gsc-client/auth-url?client_id=${id}`, { headers: h });
                            const d = await r.json();
                            if (d.auth_url) window.location.href = d.auth_url;
                            else alert('Error generando URL GSC');
                          }
                        }}
                        style={{padding:'6px 14px',background:'#6366F1',color:'white',border:'none',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                        Conectar
                      </button>
                  }
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal FB/IG Orgánico */}
      {showOrganicoModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'white',borderRadius:16,padding:32,width:520,maxWidth:'90vw',maxHeight:'80vh',overflow:'hidden',display:'flex',flexDirection:'column'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <h3 style={{fontSize:18,fontWeight:700,color:'#1e293b',margin:0}}>
                {organicoType === 'facebook' ? 'Seleccionar página de Facebook' : 'Seleccionar cuenta de Instagram'}
              </h3>
              <button onClick={() => setShowOrganicoModal(false)} style={{border:'none',background:'none',fontSize:22,cursor:'pointer',color:'#94a3b8'}}>×</button>
            </div>
            {loadingOrganico ? (
              <div style={{textAlign:'center',padding:'40px 0'}}>
                <div style={{width:36,height:36,border:'3px solid #e0e7ff',borderTopColor:'#6366f1',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 12px'}}></div>
                <p style={{color:'#94a3b8',fontSize:14}}>Cargando cuentas...</p>
              </div>
            ) : (
              <div style={{overflowY:'auto',flex:1,marginBottom:16}}>
                {organicoType === 'facebook' ? (
                  organicoPages.length === 0 ? (
                    <p style={{color:'#94a3b8',textAlign:'center',padding:20}}>No se encontraron páginas de Facebook</p>
                  ) : organicoPages.map(page => (
                    <div key={page.id} onClick={() => setSelectedFbPage(page)}
                      style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:10,border:`2px solid ${selectedFbPage?.id===page.id?'#1877F2':'#e2e8f0'}`,cursor:'pointer',background:selectedFbPage?.id===page.id?'#EFF6FF':'white',marginBottom:8}}>
                      {page.picture && <img src={page.picture} alt="" style={{width:36,height:36,borderRadius:8,objectFit:'cover'}}/>}
                      <div>
                        <p style={{margin:0,fontWeight:600,color:'#1e293b',fontSize:14}}>{page.name}</p>
                        <p style={{margin:0,color:'#94a3b8',fontSize:12}}>ID: {page.id}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  organicoIgAccounts.length === 0 ? (
                    <p style={{color:'#94a3b8',textAlign:'center',padding:20}}>No se encontraron cuentas de Instagram Business vinculadas a páginas de Facebook</p>
                  ) : organicoIgAccounts.map(ig => (
                    <div key={ig.id} onClick={() => setSelectedIgAccount(ig)}
                      style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:10,border:`2px solid ${selectedIgAccount?.id===ig.id?'#E1306C':'#e2e8f0'}`,cursor:'pointer',background:selectedIgAccount?.id===ig.id?'#FFF0F5':'white',marginBottom:8}}>
                      {ig.picture && <img src={ig.picture} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover'}}/>}
                      <div>
                        <p style={{margin:0,fontWeight:600,color:'#1e293b',fontSize:14}}>@{ig.username}</p>
                        <p style={{margin:0,color:'#94a3b8',fontSize:12}}>Vía página: {ig.page_name}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setShowOrganicoModal(false)} style={{flex:1,padding:'10px',borderRadius:8,border:'1px solid #e2e8f0',background:'white',cursor:'pointer',fontWeight:600,color:'#64748b'}}>Cancelar</button>
              <button onClick={saveOrganico} disabled={savingOrganico || (organicoType==='facebook'?!selectedFbPage:!selectedIgAccount)}
                style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:(organicoType==='facebook'?selectedFbPage:selectedIgAccount)?'#6366F1':'#e2e8f0',color:(organicoType==='facebook'?selectedFbPage:selectedIgAccount)?'white':'#94a3b8',fontWeight:700,fontSize:14,cursor:'pointer'}}>
                {savingOrganico ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal seleccionar cuenta Google Ads */}}
      {showGoogleAdsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Seleccionar cuenta Google Ads</h3>
              <button onClick={() => setShowGoogleAdsModal(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {loadingGoogleAdsAccounts ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-slate-500">Consultando cuentas en Google Ads...<br/>Puede tardar 30-60 segundos.</p>
                </div>
              ) : googleAdsAccounts.length === 0 ? (
                <p className="text-center py-12 text-slate-500">No se encontraron cuentas accesibles.</p>
              ) : (
                <div className="space-y-2">
                  {googleAdsAccounts.map(acc => (
                    <button
                      key={acc.customer_id}
                      onClick={() => selectGoogleAdsAccount(acc)}
                      disabled={savingGoogleAdsAccount || acc.is_manager}
                      className={`w-full text-left p-4 rounded-lg border transition ${acc.is_manager ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed' : 'bg-white border-slate-200 hover:border-orange-400 hover:bg-orange-50 cursor-pointer'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{acc.name}</p>
                          <p className="text-xs text-slate-500">ID: {acc.customer_id} {acc.currency && `· ${acc.currency}`}</p>
                        </div>
                        {acc.is_manager && <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded text-xs font-semibold">MCC (no seleccionable)</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal GA4 */}
      {showGa4Modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Seleccionar propiedad GA4</h3>
              <button onClick={() => setShowGa4Modal(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {loadingGa4 ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-yellow-200 border-t-yellow-600 rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-slate-500">Consultando propiedades...</p>
                </div>
              ) : ga4Properties.length === 0 ? (
                <p className="text-center py-12 text-slate-500">No se encontraron propiedades GA4.</p>
              ) : (
                <div className="space-y-2">
                  {ga4Properties.map(p => (
                    <button key={p.property_id} onClick={() => selectGa4Property(p)} disabled={savingGa4}
                      className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-yellow-400 hover:bg-yellow-50 cursor-pointer">
                      <p className="font-semibold text-slate-900">{p.property_name}</p>
                      <p className="text-xs text-slate-500">{p.account_name} · ID: {p.property_id}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal GSC */}
      {showGscModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Seleccionar sitio Search Console</h3>
              <button onClick={() => setShowGscModal(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {loadingGsc ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-slate-500">Consultando sitios...</p>
                </div>
              ) : gscSites.length === 0 ? (
                <p className="text-center py-12 text-slate-500">No se encontraron sitios GSC.</p>
              ) : (
                <div className="space-y-2">
                  {gscSites.map(s => (
                    <button key={s.site_url} onClick={() => selectGscSite(s)} disabled={savingGsc}
                      className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-green-400 hover:bg-green-50 cursor-pointer">
                      <p className="font-semibold text-slate-900">{s.site_name}</p>
                      <p className="text-xs text-slate-500">{s.site_url} {s.permission && `· ${s.permission}`}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
